'use client';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@components/ui/sheet';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Textarea } from '@components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@components/ui/select';
import ParentAttachments from '@components/files/ParentAttachments';
import { fileRowsToViews } from '@files/file-row';
import { isImageContentType } from '@files/constants';
import type { OrganizationUserRole } from '@ent/permissions-shared';
import { CAPABILITIES, hasCapability } from '@utils/capabilities';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs';
import {
  INGREDIENT_ROLES,
  SPIRIT_FAMILIES,
  type IngredientRole,
  type SpiritFamily
} from '../../menu-import/heuristics/types';
import { useQuery } from '@rocicorp/zero/react';
import { ChevronLeft, ChevronRight, ImageIcon, Sparkles } from 'lucide-react';
import { FC, FormEvent, useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { formatQuantity } from '@utils/string-utils';
import { runZeroMutation } from '~/src/zero/run-mutation';
import { useAppZero } from '~/src/zero/use-app-zero';
import { useSheetStackParams } from '@hooks/use-sheet-stack-params';
import { queries } from '~/src/zero/queries';
import { useCurrentOrgUser } from '~/src/zero/use-current-user';

type IngredientSheetProps = {
  organization_id: string;
};

const USED_IN_RECIPES_PAGE_SIZE = 6;
/** Tailwind arbitrary value: a fixed frame that never grows/shrinks with
 * content, so switching tabs or opening the generation panel never jumps
 * the sheet's height -- everything past this scrolls internally instead. */
const SHEET_HEIGHT_CLASS = 'h-[min(80vh,640px)]';

/** "base_spirit" -> "Base Spirit". Good enough without a bespoke label per value. */
function humanizeEnumValue(value: string): string {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Stacked above the recipe drawer (Radix Dialog, not vaul -- nesting two
 * vaul drawers is unreliable, see the plan's edge case 5). Self-sufficient:
 * reads recipe/ingredient state from the URL itself rather than a prop, so
 * it works whether it was opened from inside a recipe (editable quantity)
 * or standalone from search (read-only, ingredient may have many parent
 * recipes so there's no single quantity to edit -- edge case 4).
 */
const IngredientSheet: FC<IngredientSheetProps> = ({ organization_id }) => {
  const z = useAppZero();
  const { recipeId, ingredientId, closeIngredient } = useSheetStackParams();
  const t = useTranslations('menu-import.composition');
  const [saving, setSaving] = useState(false);
  const [recipesPage, setRecipesPage] = useState(0);
  const [editingDetails, setEditingDetails] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [recipeRowUntyped] = useQuery(
    recipeId && ingredientId
      ? queries.recipes.withIngredients({ recipeId })
      : undefined
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recipeRow = recipeRowUntyped as any;
  const link = recipeRow?.ingredientLinks?.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (l: any) => l.id === ingredientId
  );

  const [standaloneIngredient] = useQuery(
    !recipeId && ingredientId
      ? queries.ingredients.byId({ ingredientId })
      : undefined
  );

  const open =
    !!ingredientId && (!!link || (!recipeId && !!standaloneIngredient));

  useEffect(() => {
    setRecipesPage(0);
    setEditingDetails(false);
    setGenerating(false);
  }, [ingredientId]);

  const handleQuantitySubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!link) {
      return;
    }
    const data = new FormData(e.currentTarget);
    const quantityRaw = (data.get('quantityRaw') as string) ?? '';
    if (!quantityRaw.trim()) {
      return;
    }
    setSaving(true);
    await runZeroMutation(
      z.mutate.recipeIngredients.update({
        id: link.id,
        quantityRaw,
        now: Date.now()
      }),
      { errorTitle: 'Could not save ingredient' }
    );
    setSaving(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ingredientData: any = link
    ? link.ingredient
    : (standaloneIngredient as any);
  const ingredientId_ = link ? link.ingredient?.id : ingredientId;
  const preparation = ingredientData?.preparationRecipe;
  const title = ingredientData?.fullName;
  const brand = ingredientData?.brandName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const usedInRecipes = ((standaloneIngredient as any)?.recipeLinks ?? [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((rl: any) => rl.recipe)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((r: any) => !!r);
  const ingredientFiles = fileRowsToViews(ingredientData?.files);
  const heroImage = ingredientFiles.find(file =>
    isImageContentType(file.contentType)
  );
  // A shared/seeded ingredient (e.g. from the classic library) has no org
  // edge at all, so no one can edit/generate for it -- hide those controls
  // rather than offer ones that always error on submit.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const owningOrganizationId: string | undefined = (
    ingredientData?.viaOrgEdge as any
  )?.[0]?.sourceId;
  const isOrgOwnedIngredient = !!owningOrganizationId;
  // The org that owns this ingredient isn't always the org whose URL
  // you're viewing it from (e.g. a recipe from your own org, browsed
  // while a different org is selected) -- capability and the actual
  // edit/generate requests both need to key off the *owning* org, not
  // `organization_id` (the page's), or a caller who legitimately manages
  // the owning org would incorrectly see the controls hidden, or worse,
  // see them shown and then have the request rejected server-side.
  const orgUser = useCurrentOrgUser(owningOrganizationId ?? organization_id);
  const canManageRecipes = hasCapability(
    orgUser?.role as OrganizationUserRole | undefined,
    CAPABILITIES.ManageRecipes
  );
  const canEditIngredient = canManageRecipes && isOrgOwnedIngredient;
  const recipesPageCount = Math.max(
    1,
    Math.ceil(usedInRecipes.length / USED_IN_RECIPES_PAGE_SIZE)
  );
  const pagedRecipes = usedInRecipes.slice(
    recipesPage * USED_IN_RECIPES_PAGE_SIZE,
    (recipesPage + 1) * USED_IN_RECIPES_PAGE_SIZE
  );

  const handleDetailsSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ingredientId_) {
      return;
    }
    const data = new FormData(e.currentTarget);
    const fullName = (data.get('fullName') as string)?.trim();
    if (!fullName || !owningOrganizationId) {
      return;
    }
    setSaving(true);
    const saved = await runZeroMutation(
      z.mutate.ingredients.update({
        id: ingredientId_,
        organizationId: owningOrganizationId,
        fullName,
        brandName: (data.get('brandName') as string)?.trim() || null,
        description: (data.get('description') as string)?.trim() || null,
        notes: (data.get('notes') as string)?.trim() || null,
        role: (data.get('role') as IngredientRole) || null,
        roleFamily: (data.get('roleFamily') as SpiritFamily) || null,
        now: Date.now()
      }),
      { errorTitle: 'Could not save ingredient' }
    );
    setSaving(false);
    if (saved) {
      setEditingDetails(false);
    }
  };

  const renderDetails = () => {
    if (!ingredientData) return null;
    if (editingDetails) {
      return (
        <form
          key={`edit-${ingredientId_}`}
          onSubmit={handleDetailsSubmit}
          className="flex flex-col gap-3"
        >
          <div>
            <label className="mb-1 block text-label text-muted-foreground">
              Full name
            </label>
            <Input name="fullName" defaultValue={title ?? ''} required />
          </div>
          <div>
            <label className="mb-1 block text-label text-muted-foreground">
              Brand
            </label>
            <Input name="brandName" defaultValue={brand ?? ''} />
          </div>
          <div>
            <label className="mb-1 block text-label text-muted-foreground">
              Description
            </label>
            <Textarea
              name="description"
              defaultValue={ingredientData.description ?? ''}
              rows={2}
            />
          </div>
          <div>
            <label className="mb-1 block text-label text-muted-foreground">
              Notes
            </label>
            <Textarea
              name="notes"
              defaultValue={ingredientData.notes ?? ''}
              rows={2}
            />
          </div>
          <div>
            <label className="mb-1 block text-label text-muted-foreground">
              Role
            </label>
            <Select name="role" defaultValue={ingredientData.role ?? ''}>
              <SelectTrigger>
                <SelectValue placeholder="Unset" />
              </SelectTrigger>
              <SelectContent>
                {INGREDIENT_ROLES.map(role => (
                  <SelectItem key={role} value={role}>
                    {humanizeEnumValue(role)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-label text-muted-foreground">
              Spirit family
            </label>
            <Select
              name="roleFamily"
              defaultValue={ingredientData.roleFamily ?? ''}
            >
              <SelectTrigger>
                <SelectValue placeholder="Unset" />
              </SelectTrigger>
              <SelectContent>
                {SPIRIT_FAMILIES.map(family => (
                  <SelectItem key={family} value={family}>
                    {humanizeEnumValue(family)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => setEditingDetails(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-4">
          <div className="flex shrink-0 flex-col items-start gap-2">
            <div className="flex size-24 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
              {heroImage ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={heroImage.url}
                  alt={title ?? 'Ingredient'}
                  className="size-full object-cover"
                />
              ) : (
                <ImageIcon className="size-6 text-muted-foreground" />
              )}
            </div>
            {canEditIngredient && ingredientId_ ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="px-5 py-2.5"
                onClick={() => setGenerating(true)}
              >
                <Sparkles className="size-4" />
                Generate
              </Button>
            ) : null}
          </div>
          <dl className="flex-1 space-y-2 text-body-dense">
            <div>
              <dt className="text-label text-muted-foreground">Full name</dt>
              <dd className="text-foreground">{title || '—'}</dd>
            </div>
            <div>
              <dt className="text-label text-muted-foreground">Brand</dt>
              <dd className="text-secondary-foreground">{brand || '—'}</dd>
            </div>
          </dl>
        </div>
        <div>
          <p className="text-label text-muted-foreground">Description</p>
          <p className="text-body-dense text-secondary-foreground">
            {ingredientData.description || '—'}
          </p>
        </div>
        <div>
          <p className="text-label text-muted-foreground">Notes</p>
          <p className="text-body-dense text-secondary-foreground">
            {ingredientData.notes || '—'}
          </p>
        </div>
        <div className="flex gap-6">
          <div>
            <p className="text-label text-muted-foreground">Role</p>
            <p className="text-body-dense text-secondary-foreground">
              {ingredientData.role
                ? humanizeEnumValue(ingredientData.role)
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-label text-muted-foreground">Spirit family</p>
            <p className="text-body-dense text-secondary-foreground">
              {ingredientData.roleFamily
                ? humanizeEnumValue(ingredientData.roleFamily)
                : '—'}
            </p>
          </div>
        </div>
        {canEditIngredient ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start px-5 py-2.5"
            onClick={() => setEditingDetails(true)}
          >
            Edit
          </Button>
        ) : null}
        {canManageRecipes && !isOrgOwnedIngredient ? (
          <p className="text-meta text-muted-foreground">
            Editing and image generation aren&apos;t available for a shared
            library ingredient.
          </p>
        ) : null}
      </div>
    );
  };

  const renderGeneration = () =>
    ingredientId_ ? (
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start px-5 py-2.5"
          onClick={() => setGenerating(false)}
        >
          <ChevronLeft className="size-4" />
          Back to details
        </Button>
        <ParentAttachments
          organizationId={owningOrganizationId ?? organization_id}
          parentId={ingredientId_}
          parentType="ingredients"
          files={ingredientFiles}
          canEdit={canEditIngredient}
          generation={canEditIngredient ? { kindHint: 'product' } : undefined}
        />
      </div>
    ) : null;

  return (
    <Sheet
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          closeIngredient();
        }
      }}
    >
      <SheetContent
        side="bottom"
        className={`mx-auto flex ${SHEET_HEIGHT_CLASS} max-w-md flex-col overflow-hidden rounded-t-xl`}
      >
        <SheetHeader>
          <SheetTitle>{title ?? 'Ingredient'}</SheetTitle>
          {brand ? <SheetDescription>{brand}</SheetDescription> : null}
        </SheetHeader>
        {preparation ? (
          <Link
            className="text-brand-text underline underline-offset-4"
            href={`/${organization_id}/bev/recipes/${preparation.id}`}
          >
            {preparation.name} ·{' '}
            {t(
              preparation.preparationInferred && preparation.status === 'draft'
                ? 'inferred'
                : 'linked'
            )}
          </Link>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col pt-4">
          {link ? (
            <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1">
              <form
                key={link.id}
                onSubmit={handleQuantitySubmit}
                className="flex items-end gap-2"
              >
                <div className="flex-1">
                  <label
                    htmlFor="quantityRaw"
                    className="mb-1 block text-label text-muted-foreground"
                  >
                    Quantity
                  </label>
                  <Input
                    id="quantityRaw"
                    name="quantityRaw"
                    defaultValue={formatQuantity(
                      link.quantityStr ?? '',
                      link.quantityType ?? ''
                    )}
                    placeholder="e.g. 2 oz"
                  />
                </div>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </form>
              {generating ? renderGeneration() : renderDetails()}
            </div>
          ) : standaloneIngredient ? (
            <Tabs
              defaultValue="details"
              className="flex min-h-0 flex-1 flex-col"
            >
              <TabsList className="w-full shrink-0">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="recipes">
                  Recipes ({usedInRecipes.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="details"
                className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1"
              >
                {generating ? renderGeneration() : renderDetails()}
              </TabsContent>
              <TabsContent
                value="recipes"
                className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1"
              >
                <p className="mb-2 text-body-dense text-muted-foreground">
                  Open a recipe to edit its quantity there.
                </p>
                <ul className="divide-y divide-border rounded-md border">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {pagedRecipes.map((recipe: any) => (
                    <li key={recipe.id}>
                      <Link
                        href={`/${organization_id}/bev/recipes/${recipe.id}`}
                        className="flex min-h-11 items-center p-3 text-body-dense hover:bg-accent"
                        onClick={() => closeIngredient()}
                      >
                        {recipe.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                {recipesPageCount > 1 ? (
                  <div className="mt-2 flex items-center justify-between text-body-dense text-muted-foreground">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="px-5 py-2.5"
                      disabled={recipesPage === 0}
                      onClick={() =>
                        setRecipesPage(page => Math.max(0, page - 1))
                      }
                    >
                      <ChevronLeft className="size-4" />
                      Previous
                    </Button>
                    <span>
                      Page {recipesPage + 1} of {recipesPageCount}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="px-5 py-2.5"
                      disabled={recipesPage >= recipesPageCount - 1}
                      onClick={() =>
                        setRecipesPage(page =>
                          Math.min(recipesPageCount - 1, page + 1)
                        )
                      }
                    >
                      Next
                      <ChevronRight className="size-4" />
                    </Button>
                  </div>
                ) : null}
              </TabsContent>
            </Tabs>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default IngredientSheet;
